/**
 * [PM-SG 2026-08-11 · redesigned 2026-08-12] PIT WALL — the five headline figures, on a lap.
 *
 * THE FAULT THIS FIXES. The page delivered its five numbers in three seconds and then died: a
 * photograph of a dashboard, on a wall, for the rest of the day. Nothing on it encoded the
 * passage of time, two of the five cards reserved a caption line that never arrived (hence the
 * hollow look), and the page was named after an object it did not behave like — a pit board is
 * not a poster, it is held out through the wall EVERY LAP and its numbers are physically slid
 * into slots as they change.
 *
 * THE PIT BOARD LAP. The page now runs on the same 30-second beat as RACE, and only one thing
 * moves at a time — the discipline that separates a broadcast graphic from a fairground:
 *
 *   0.0s  LIGHTS    Each card owns one lamp. They illuminate one per second, hold, and go out
 *                   ALL AT ONCE. Five lights is the start of every Grand Prix, and this page
 *                   happens to have exactly five cards — the mapping was sitting there unused.
 *   3.7s  DELIVERY  Lights out, so the cars go: one crosses each card and the figure is
 *                   uncovered in its wake. The board is being LAID DOWN, not switched on.
 *   7.1s  ON AIR    One card at a time takes the screen for five seconds — it lifts, its rail
 *                   lights, a sweep crosses it — then hands over. Round and round. This is the
 *                   beat that keeps the page alive without adding a single new figure to it.
 *
 * WHAT IS AND IS NOT ALLOWED TO MOVE. Anything that loops encodes nothing (the lamp sequence,
 * the rail highlight, the kerbs). Anything that encodes something fires once,
 * when it actually happened — a figure counts up and its card flashes ONLY when that figure
 * really changed between polls, never on first paint. Nothing ever moves underneath a number
 * while it is being read: the on-air card lifts and brightens, the figure itself sits still.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  SideCarSvg, SIDE_CAR_RATIO, Exhaust, INK_2, LINE, TEXT, RED, EASE, kerb, prefersReducedMotion,
} from './chrome';
import { Kpi } from './raceModel';

/* ═════════════════════════════════════════════════════════════ the lap ═════ */

/**
 * THE FORM-UP. Cards into place and lamps lit, and then the cars go — all inside ¾ of a second.
 *
 * [2026-08-12] ⚠ THIS USED TO BE 3.7 SECONDS and it was wrong. It ran the real gantry's cadence
 * — one lamp per second, then a hold — which meant anybody arriving on this page watched five
 * empty cards for nearly four seconds before the first car appeared. On a rotation that is not
 * drama, it is a page that looks broken.
 *
 * The lamps survive because lights-out is what sends the cars, but they now run WITH the cards
 * arriving rather than before them: by the time the last card has landed the last lamp is lit,
 * and the release follows immediately. The slowness the user wanted belongs to the CAR — the
 * thing that actually uncovers a figure — not to a wait in front of an empty board.
 */
const CARD_STEP = 60;
const CARD_MS = 520;
const LAMP_STEP = 110;
/** Cards are in place, all five lamps are lit — lights out, and the delivery begins. */
const LIGHTS_MS = CARD_STEP * 4 + CARD_MS + 20;

/**
 * THE DELIVERY RUN, and these four numbers are one system — change one and re-derive the rest.
 *
 * [2026-08-12] Slowed from 1500ms at the user's request. The car is not just decoration here: it
 * is what UNCOVERS the figure, so its speed is the speed the number becomes readable at. At
 * 1500ms across a card the length of half a wall it was a flick, and the eye arrived after the
 * reveal had already finished.
 *
 * The wake must stay BEHIND the car — it starts a fifth of the way into the run and finishes
 * before the car leaves, which is what makes the figure look uncovered rather than faded in.
 */
const CAR_MS = 2200;
/**
 * ⚠ ONE CAR ON THE BOARD AT A TIME. The step IS the run, so the next card's car does not exist
 * until the previous one has left its card — asked for explicitly, and it is the right call: at
 * a 520ms stagger there were four cars on screen together and the delivery read as traffic
 * rather than as five separate figures being laid down.
 *
 * The cost is honest and unavoidable — the full sequence is now five runs end to end, about
 * eleven seconds. Cars cannot be both slow and simultaneous. If that ever needs to come down,
 * CAR_MS is the dial; everything below re-derives from it.
 */
const CAR_STEP = CAR_MS;
/* The wake still has to start inside its own car's run AND finish before that car leaves, or it
   bleeds into the next card's turn: 440 + 1500 = 1940ms, comfortably inside 2200ms. */
const WAKE_DELAY = 440;
const WAKE_MS = 1500;
/** Long enough for the LAST car to have crossed its card: 4 × step + the run itself, + margin. */
const DELIVER_MS = CAR_STEP * 4 + CAR_MS + 220;
/** How long each card holds the screen once the board is live. */
const AIR_MS = 5000;

/**
 * [2026-08-12] THE WHOLE SEQUENCE REPLAYS EVERY THIRTY SECONDS.
 *
 * The lights and the delivery used to fire once per visit and never again, so a page that the
 * rotation holds for a minute spent its second half as a still image. Re-running the lap is what
 * the board does everywhere else — RACE laps its field every thirty seconds — and it means
 * somebody who walks up midway through still sees the board being laid down rather than a
 * photograph of it.
 *
 * ⚠ It must stay comfortably longer than the sequence itself: lights (0.8s) plus the delivery
 * (11.2s) is about twelve seconds, leaving roughly eighteen for the on-air rotation before the
 * cars come round again. Shorten CAR_MS and this can come down with it; lengthen it and this has
 * to grow, or a replay will cut its own delivery off mid-run.
 */
const REPLAY_MS = 30000;

type Stage = 'lights' | 'deliver' | 'live';

/* ═════════════════════════════════════════════════════════════ motion ═════ */

const cardIn = keyframes`from { opacity: 0; transform: translateY(26px) scale(.985); }`;
/**
 * ⚠ [PM-SG SMOOTH 2026-08-13] Was an animation on `left`, which relayouts every frame. The car
 * keeps its static `left: -24%` and travels by transform instead.
 *
 * The distance is stated in `cqw` rather than `%` on purpose: a translateX percentage resolves
 * against the ELEMENT, and this car's width comes from `height x aspect-ratio`, so there is no
 * percentage of itself that equals the trip. `cqw` resolves against the card, which is the same
 * box `left` was measured against — so -24% -> 116% is exactly 140cqw of travel, unchanged.
 * The card already declares `container-type: size` for the `cqh` sizing above.
 *
 * translateY(-50%) is restated because `transform` is a single property: naming it in the
 * keyframes would otherwise drop the vertical centring the base rule sets.
 */
const drive = keyframes`
  0%   { transform: translateX(0) translateY(-50%); opacity: 0; }
  12%  { opacity: 1; }
  84%  { opacity: 1; }
  100% { transform: translateX(140cqw) translateY(-50%); opacity: 0; }
`;
/**
 * The wake: the figure is uncovered from the left, behind the car.
 *
 * ⚠ [PM-SG SMOOTH 2026-08-13] DELIBERATELY STILL clip-path. Chrome paints this on the main thread
 * and everything else on the board was moved off it — this one stays, and the reason is worth
 * writing down so nobody "finishes the job" and breaks it.
 *
 * It is applied to the FIGURES THEMSELVES (.pwmid, .k3), so the compositor-friendly swaps do not
 * apply: `scaleX` would squash the number rather than uncover it, and a retracting cover would
 * have to repaint the card's own gradient behind it to stay invisible. What makes it acceptable
 * is scale — a few small text boxes for WAKE_MS once per replay cycle, versus the full-screen
 * page wipe that WAS the problem and is now a transform. Cheap where it is; leave it.
 */
const lay = keyframes`
  from { clip-path: inset(0 100% 0 0); }
  to   { clip-path: inset(0 -8% 0 0); }
`;
const pip = keyframes`from { opacity: 0; transform: scaleY(.2); }`;
/** The strip that runs the card's top edge as the car passes it. */
/**
 * ⚠ [PM-SG SMOOTH 2026-08-13] The origin used to FLIP mid-animation (left for the grow, right for
 * the retract) which is the usual way to write this — but `transform-origin` is not an animatable
 * compositor property, so every frame after the flip went back to the main thread.
 *
 * Same two-stage wipe, one fixed origin: the origin stays `left center` (set on the element), the
 * grow is a plain scaleX, and the retract-to-the-right is expressed as translateX(100%) scaleX(0)
 * — the translate puts the collapsing edge at the strip's right end, which is what flipping the
 * origin was doing. Percentages in translateX resolve against the UNSCALED width, so 100% is
 * exactly the strip's own length.
 */
const stripRun = keyframes`
  0%   { transform: translateX(0) scaleX(0); opacity: 0; }
  12%  { opacity: 1; }
  55%  { transform: translateX(0) scaleX(1); opacity: 1; }
  56%  { transform: translateX(0) scaleX(1); }
  100% { transform: translateX(100%) scaleX(0); opacity: 0; }
`;
/** The only continuous card-level motion: a highlight sliding down the accent rail. */
const railRun = keyframes`
  from { transform: translateY(-140%); }
  to   { transform: translateY(340%); }
`;
/** On air: a single pass of light across the card that has the screen. */
const sweep = keyframes`
  from { transform: translateX(-120%) skewX(-14deg); }
  to   { transform: translateX(320%) skewX(-14deg); }
`;
/** News, and only news. */
const newsFlash = keyframes`
  0%   { box-shadow: inset 0 0 0 2px var(--a, ${RED}), inset 0 0 70px -20px var(--a, ${RED}); }
  100% { box-shadow: inset 0 0 0 2px transparent, inset 0 0 70px -20px transparent; }
`;
const settle = keyframes`0% { transform: scale(1.06); } 100% { transform: scale(1); }`;

/* ═════════════════════════════════════════════════════════════ layout ═════ */

const View = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  &::after {
    content: '';
    position: absolute; inset: 0; pointer-events: none; z-index: 4;
    background: radial-gradient(ellipse 62% 58% at 50% 44%, rgba(225, 6, 0, 0.09), transparent 72%);
  }
`;

const PwKerb = styled.div`
  flex: 0 0 9px;
  z-index: 5;
  ${kerb}
  &:last-child::after { animation-delay: -4.5s; }
`;

/* [2026-08-12] THE CIRCUIT WATERMARK IS GONE. A stylised street layout was drawn across the
   panel with one light lapping it, on the reasoning that PM Singapore is a night street race.
   The five cards cover essentially the whole panel, so all it ever amounted to was a few pixels
   of glow in the gutters between them — ornament nobody could see, and a 30s animation running
   for ever to produce it. */

const Grid = styled.div<{ $stage: Stage }>`
  position: relative;
  z-index: 3;
  flex: 1;
  min-height: 0;
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: 1fr 1fr;
  padding: 30.24px 34px;

  /* Placed by index, so the render order is left alone and only the arrangement changes. */
  > *:nth-child(1) { grid-area: 1 / 1 / 2 / 3; }  /* placed          */
  > *:nth-child(2) { grid-area: 1 / 3 / 2 / 5; }  /* retention       */
  > *:nth-child(3) { grid-area: 1 / 5 / 2 / 7; }  /* classified      */
  > *:nth-child(4) { grid-area: 2 / 1 / 3 / 4; }  /* best efficiency */
  > *:nth-child(5) { grid-area: 2 / 4 / 3 / 7; }  /* leading         */

  /* [PM-SG FILL 2026-08-13] The 900px stacking rule is gone. It measured the VIEWPORT while this
     grid is laid out on a canvas clamped to 1600 wide, so it could only ever fire on the wrong
     signal — never on a television, and spuriously on a small laptop where the canvas was still
     full width. Six columns always fit here now, and a wider screen only gives them more room. */

  /* THE GRID FORMS UP while the lamps are lighting: the cards are there, the figures are not.
     Holding the numbers back is what makes the delivery mean anything. */
  ${(p) => p.$stage === 'lights' && css`
    .pwslab { animation: ${cardIn} ${CARD_MS}ms ${EASE} backwards;
              animation-delay: calc(var(--i) * ${CARD_STEP}ms); }
    .pwmid, .k3, .pwpips { clip-path: inset(0 100% 0 0); }
  `}

  /* LIGHTS OUT — the cars go. Tighter stagger than a queue: five cars leaving a grid. */
  ${(p) => p.$stage === 'deliver' && css`
    .pwcar   { animation: ${drive} ${CAR_MS}ms cubic-bezier(.36,.5,.42,1) backwards;
               animation-delay: calc(var(--i) * ${CAR_STEP}ms); }
    .pwstrip { animation: ${stripRun} ${CAR_MS}ms cubic-bezier(.36,.5,.42,1) backwards;
               animation-delay: calc(var(--i) * ${CAR_STEP}ms); }
    /* Starts a fifth of the way into its car's run, so the figure is uncovered BEHIND it. */
    .pwmid, .k3 { animation: ${lay} ${WAKE_MS}ms cubic-bezier(.3,.66,.3,1) backwards;
                  animation-delay: calc(var(--i) * ${CAR_STEP}ms + ${WAKE_DELAY}ms); }
    /* The pips are the one figure with a natural sequence, so they are lit one at a time rather
       than wiped in as a block — a gate cleared is an event, and four of them are four events. */
    .pwpips i { animation: ${pip} 460ms ${EASE} backwards;
                animation-delay: calc(var(--i) * ${CAR_STEP}ms + ${WAKE_DELAY + 80}ms
                                      + var(--q) * 120ms); }
    .pwroll  { animation: ${settle} 460ms ${EASE} backwards;
               animation-delay: calc(var(--i) * ${CAR_STEP}ms + ${WAKE_DELAY + WAKE_MS - 200}ms); }
  `}
`;

const Slab = styled.div<{ $air: boolean; $news: boolean }>`
  /* A query container, so the figure inside can be sized against THIS box rather than the
     viewport — the only way a number is guaranteed to fit whatever column it lands in. */
  container-type: size;
  position: relative;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 28px;
  padding-left: 36px;
  background: linear-gradient(180deg, ${INK_2}, #191922);
  border: 1px solid ${LINE};
  /* Chamfered, not rounded: the board's geometry is cut corners throughout, and a 5px radius
     was the last piece of generic-dashboard styling left on this page. */
  clip-path: polygon(
    0 0, calc(100% - 19.2px) 0, 100% 19.2px,
    100% 100%, 19.2px 100%, 0 calc(100% - 19.2px));
  transition: transform 640ms ${EASE}, border-color 640ms ${EASE}, box-shadow 640ms ${EASE};

  /* ON AIR. The card lifts and lights; nothing inside it moves, because the whole point of the
     card is the figure and a figure that jitters cannot be read from across a room. */
  ${(p) => p.$air && css`
    transform: translateY(-5.94px);
    border-color: rgba(255, 255, 255, 0.24);
    box-shadow: 0 0 0 1px var(--a, rgba(255,255,255,.3)), 0 21.6px 54px -25.92px var(--a, #000);
  `}
  ${(p) => p.$news && css`animation: ${newsFlash} 2200ms ${EASE};`}

  /* The accent rail, and the one highlight that travels it. */
  &::before {
    content: '';
    position: absolute; left: 0; top: 0; bottom: 0;
    width: 5.76px;
    background: var(--a, rgba(255, 255, 255, 0.18));
    opacity: ${(p) => (p.$air ? 1 : 0.8)};
    transition: opacity 640ms ${EASE};
  }
  .pwrail {
    position: absolute; left: 0; top: 0; z-index: 2;
    width: 5.76px; height: 34%;
    background: linear-gradient(180deg, transparent, rgba(255, 255, 255, 0.9), transparent);
    animation: ${railRun} 7.5s linear infinite;
    will-change: transform;
    animation-delay: calc(var(--i) * -1.4s);
    pointer-events: none;
  }
  .pwstrip {
    position: absolute; left: 0; right: 0; top: 0; height: 2px; z-index: 2;
    background: linear-gradient(90deg, transparent, var(--a, ${TEXT}), transparent);
    box-shadow: 0 0 18px var(--a, ${TEXT});
    opacity: 0;
    /* [PM-SG SMOOTH 2026-08-13] Fixed origin — stripRun no longer flips it mid-run. */
    transform-origin: left center;
    will-change: transform, opacity;
    pointer-events: none;
  }
  /* The on-air pass of light. Keyed off the same prop as the lift, so it fires once per turn. */
  ${(p) => p.$air && css`
    .pwsweep {
      position: absolute; top: 0; bottom: 0; left: 0; z-index: 2;
      width: 28%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.07), transparent);
      animation: ${sweep} 1500ms ${EASE};
      pointer-events: none;
    }
  `}

  /**
   * THE LAMP. Same object as the boot screen's start lights, so the board's own language is
   * consistent from the moment it opens: dead red glass, then lit, then out for all five at
   * once. Placed top-right, where it cannot collide with a label or a figure.
   */
  .pwlamp {
    position: absolute; z-index: 2;
    top: 22px; right: 22px;
    width: clamp(9px, 1.5cqh, 20px); height: clamp(9px, 1.5cqh, 20px);
    border-radius: 50%;
    background: #2A1114;
    box-shadow: inset 0 -2px 5px rgba(0, 0, 0, 0.55);
    transition: background 140ms linear, box-shadow 140ms linear;
  }
  .pwlamp.on {
    background: ${RED};
    box-shadow: 0 0 22px rgba(225, 6, 0, 0.95), inset 0 -3px 7px rgba(0, 0, 0, 0.45);
  }

  /* Both captions are read from across a room, so they are set at full white and given real
     size — a 14px grey label under a wall-sized number is a desk layout on a television. */
  /* [2026-08-12] Both captions stepped up about a quarter (2.6cqw → 3.2cqw, ceiling 24 → 30px).
     They are read from the same distance as the figure and were sized like desk labels. */
  .k1 {
    flex: 0 0 auto;
    font-size: max(37px, min(3.2cqw, 8cqh));
    font-weight: 700; letter-spacing: 0.2em; line-height: 1.25;
    color: ${TEXT};
    padding-right: clamp(22px, 3cqh, 44px); /* clear of the lamp */
  }
  /* THE FIGURE OWNS THE MIDDLE. It used to be pushed to the top by space-between, which is what
     left that trench of empty card under it on a 65" panel. */
  .pwmid {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    align-items: center;
  }
  .k2 {
    font-size: min(17cqw, 40cqh);
    font-weight: 900; line-height: 1; letter-spacing: -0.02em;
    white-space: nowrap;
  }
  .k2 .pwroll { display: inline-block; transform-origin: left center; }
  /* A name is not a number: it wants a readable weight and the freedom to wrap. */
  .k2.sm {
    font-size: min(8cqw, 21cqh);
    line-height: 1.08; letter-spacing: 0; white-space: normal; overflow-wrap: anywhere;
  }
  /* [2026-09-18, user] Still a figure — nowrap, same weight and tracking — just trimmed. The
     bottom row is twice as wide as the top, so 40cqh binds there and 17cqw binds up top, and a
     bottom-row value comes out around half again as large for no reason the room can see. */
  .k2.md { font-size: min(13cqw, 30cqh); }
  .k3 {
    flex: 0 0 auto;
    font-size: max(37px, min(3.2cqw, 8cqh));
    font-weight: 700; letter-spacing: 0.14em; line-height: 1.25;
    color: ${TEXT}; opacity: 0.85; min-height: 1em;
  }

  /* The gate count is the one figure with a natural visual form: one pip per PM, lit as they
     clear. Built from numbers already in the payload rather than parsed back out of "4 / 10". */
  .pwpips { flex: 0 0 auto; display: flex; gap: 4px; align-items: center; margin-top: 10px; }
  .pwpips i {
    flex: 1 1 0; height: 9.72px; border-radius: 1px;
    background: rgba(255, 255, 255, 0.1); transition: background 0.4s ${EASE};
    transform-origin: bottom center;
  }
  .pwpips i.on { background: var(--a); box-shadow: 0 0 14px var(--a); }

  /**
   * ⚠ THE PROFILE CAR IS THREE TIMES ITS OWN HEIGHT, where the old top-down icon was under two.
   * Height came down accordingly, or the car is longer than half the card it is crossing.
   */
  .pwcar {
    position: absolute; top: 50%; left: -24%; transform: translateY(-50%);
    /* [PM-SG SMOOTH 2026-08-13] drive() moves this by transform now, not by the left property. */
    will-change: transform, opacity;
    height: clamp(20px, 9cqh, 52px); aspect-ratio: ${SIDE_CAR_RATIO};
    color: var(--a, ${RED}); opacity: 0; z-index: 4; pointer-events: none;
    filter: drop-shadow(0 0 14px var(--a, ${RED})) drop-shadow(0 5px 11px rgba(0, 0, 0, 0.65));
  }
  .pwcar svg { width: 100%; height: 100%; display: block; position: relative; z-index: 2; }
`;

/* ────────────────────────────────────────────── the odometer roll ── */

/* ⚠ The currency prefix is part of this test, not just of the formatting. A value of "$36,319"
   fails a purely numeric pattern, and a figure that fails it is SET OUTRIGHT instead of counting
   up — so adding a $ to the money KPIs would have silently killed the odometer on the only two
   cards it was written for. */
const ROLLABLE = /^-?\$?[\d,]+(\.\d+)?%?$/;

/** Re-dresses a number in the sample's own clothes: its decimals, its $, its %. */
function fmtLike(n: number, sample: string): string {
  const pct = /%$/.test(sample);
  const cur = sample.includes('$');
  const body = sample.replace(/[$%]/g, '');
  const dec = (body.split('.')[1] || '').length;
  const p = Math.abs(n).toFixed(dec).split('.');
  p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Minus outside the symbol — "-$875", never "$-875".
  return (n < 0 ? '-' : '') + (cur ? '$' : '') + p.join('.') + (pct ? '%' : '');
}

/** Counts a figure up to its new value rather than swapping it. Only for values that are
 *  actually numeric — a name is set outright. */
/**
 * ⚠ [PM-SG SMOOTH 2026-08-13] THE COUNT-UP WRITES TO THE DOM, NOT TO REACT STATE.
 *
 * It used to call setShown() inside the rAF loop, which is one React render — reconciliation and
 * all — per frame, per odometer. Five of these run together on this card, so at 60Hz that was
 * ~300 renders a second and at 120Hz it would have been ~600, all on the main thread, all while
 * the cars are driving across the same screen. The frame budget at 120Hz is 8.3ms; this was
 * spending it on re-deriving a component tree whose only actual change was the text inside one
 * span.
 *
 * The text node is now written directly. React still owns the element and still renders it on a
 * real value change (see the span's initial content); the tween in between is just characters
 * going into a node the compositor never has to re-composite. Identical result on screen, no
 * render cost.
 */
const Rolling: React.FC<{ value: string; small?: boolean; mid?: boolean }> = ({ value, small, mid }) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const prev = useRef<number | null>(null);
  const raf = useRef(0);

  useEffect(() => {
    const el = spanRef.current;
    if (!el) return;
    const write = (t: string) => { el.textContent = t; };

    if (!ROLLABLE.test(value)) { write(value); prev.current = null; return; }
    const to = parseFloat(value.replace(/[$,%]/g, ''));
    const from = prev.current === null ? 0 : prev.current;
    prev.current = to;
    if (from === to) { write(value); return; }

    let start: number | null = null;
    const dur = 950;
    const step = (ts: number) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / dur);
      const e = 1 - Math.pow(1 - p, 3); // ease-out cubic
      write(fmtLike(from + (to - from) * e, value));
      if (p < 1) raf.current = requestAnimationFrame(step);
      else write(value);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [value]);

  /* The span is rendered with the final value so a first paint (and any React re-render that
     happens mid-tween) shows something correct rather than empty; the effect above takes over
     the text node immediately afterwards. */
  const cls = small ? 'k2 sm' : mid ? 'k2 md' : 'k2';
  return <div className={cls}><span className="pwroll" ref={spanRef}>{value}</span></div>;
};

/* ─────────────────────────────────────────────────────────── view ── */

const PitWallView: React.FC<{ kpis: Kpi[]; entryToken: number }> = ({ kpis, entryToken }) => {
  const reduced = useRef(prefersReducedMotion());
  const [stage, setStage] = useState<Stage>(reduced.current ? 'live' : 'lights');
  const [lit, setLit] = useState(0);
  const [air, setAir] = useState(-1);
  const [news, setNews] = useState<Set<string>>(new Set());
  const [cycle, setCycle] = useState(0);
  const seen = useRef<Map<string, string>>(new Map());

  /* The lap. Restarted on every visit so the first replay is a full interval after arrival, not
     the remainder of a clock that has been running since the board booted. */
  useEffect(() => {
    if (reduced.current) return;
    const t = window.setInterval(() => setCycle((n) => n + 1), REPLAY_MS);
    return () => window.clearInterval(t);
  }, [entryToken]);

  /* THE START. Restarts rather than resumes, so opening the page a second time looks like
     opening it the first — it is put on screen by a rotation, not by a person. */
  useEffect(() => {
    if (reduced.current) return;
    setStage('lights');
    setLit(0);
    setAir(-1);
    const timers: number[] = [];
    // Each lamp lights as its own card lands, so the sequence costs the page no waiting at all.
    for (let i = 0; i < 5; i++) {
      timers.push(window.setTimeout(() => setLit(i + 1), 60 + i * LAMP_STEP));
    }
    // Lights out is the whole point of the sequence: all five at once, then the cars go.
    timers.push(window.setTimeout(() => { setLit(0); setStage('deliver'); }, LIGHTS_MS));
    timers.push(window.setTimeout(() => setStage('live'), LIGHTS_MS + DELIVER_MS));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [entryToken, cycle]);

  /* ON AIR. Starts only once the board is live, so it can never fight the delivery. */
  useEffect(() => {
    if (stage !== 'live' || reduced.current || !kpis.length) return;
    setAir(0);
    const t = window.setInterval(
      () => setAir((a) => (a + 1) % kpis.length),
      AIR_MS,
    );
    return () => window.clearInterval(t);
  }, [stage, kpis.length]);

  /* News, not arrival. The first payload fills the map without flashing anything — every figure
     is "new" on the first render, and a board that flashes all five cards the moment it opens
     has taught the room to ignore the flash by the second time they see it. */
  useEffect(() => {
    const changed = new Set<string>();
    for (const k of kpis) {
      const was = seen.current.get(k.label);
      if (was !== undefined && was !== k.value) changed.add(k.label);
      seen.current.set(k.label, k.value);
    }
    if (!changed.size) return;
    setNews(changed);
    const t = window.setTimeout(() => setNews(new Set()), 2400);
    return () => window.clearTimeout(t);
  }, [kpis]);

  return (
    <View>
      <PwKerb />

      <Grid $stage={stage}>
        {kpis.map((k, i) => (
          <Slab
            key={k.label}
            className="pwslab"
            $air={air === i}
            $news={news.has(k.label)}
            style={{ '--i': i, ...(k.accent ? { '--a': k.accent } : {}) } as React.CSSProperties}
          >
            <div className="pwrail" />
            <div className="pwstrip" />
            {air === i && <div className="pwsweep" />}
            <div className={`pwlamp${lit > i ? ' on' : ''}`} />

            <div className="k1">{k.label}</div>
            <div className="pwmid">
              <Rolling value={k.value} small={k.small} mid={k.mid} />
            </div>
            <div className="k3">{k.sub}</div>

            {k.pips && (
              <div className="pwpips">
                {Array.from({ length: k.pips.of }).map((_, q) => (
                  <i
                    key={q}
                    className={q < (k.pips as { on: number }).on ? 'on' : ''}
                    style={{ '--q': q } as React.CSSProperties}
                  />
                ))}
              </div>
            )}

            {/* The plume marks which way the car is facing and where it has been — the same cue
                it gives on the race track. idSuffix keeps each card's gradients out of the next
                card's: five cars sharing one gradient id is one car's livery on all five. */}
            <div className="pwcar">
              <Exhaust><i /><i /><i /><i /><i /><i /></Exhaust>
              <SideCarSvg idSuffix={`pw${i}`} />
            </div>
          </Slab>
        ))}
      </Grid>

      <PwKerb />
    </View>
  );
};

/**
 * [PM-SG SMOOTH 2026-08-13] Memoised. The board shell re-renders on its own state — the page
 * rotation tick, the sweep, an announcement, the diagnostic toggle, a resize — none of which
 * change this view's props. Without this, each of those re-rendered the whole view mid-animation.
 */
export default React.memo(PitWallView);
